import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/blog/shopify-apps-auswaehlen";
const TITLE = "Shopify Apps auswählen: weniger installieren, mehr verdienen";
const DESCRIPTION =
  "Welche Shopify Apps sich lohnen, welche den Shop verlangsamen und wann eine Theme-Anpassung günstiger ist als ein Abo. Mit Prüfliste vor jeder Installation.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Jede Shopify App kostet monatlich Geld und meist auch Ladezeit, weil sie eigenes JavaScript in den Shop lädt. Sinnvoll sind Apps für Aufgaben, die tief in Shopify eingreifen, etwa Zahlung, Versand, Buchhaltung oder Abo-Abwicklung. Für Anzeige und Layout ist eine einmalige Theme-Anpassung oft günstiger als ein dauerhaftes Abo und deutlich schneller im Ladeverhalten.";

const faqs = [
  {
    q: "Verlangsamen Shopify Apps den Shop?",
    a: "Viele ja. Apps binden zusätzliche Skripte und Stylesheets ein, die auf jeder Seite geladen werden, auch dort, wo die App gar nicht gebraucht wird. Mehrere solcher Apps zusammen sind ein häufiger Grund für langsame Produktseiten.",
  },
  {
    q: "Wie viele Apps sind normal?",
    a: "Kleinere Shops kommen meist mit fünf bis zehn Apps aus. Wenn die Liste deutlich länger ist, lohnt eine Durchsicht: Häufig laufen Abos für Funktionen weiter, die niemand mehr nutzt oder die das Theme inzwischen selbst kann.",
  },
  {
    q: "Wann ist eine Theme-Anpassung besser als eine App?",
    a: "Immer dann, wenn es um Darstellung geht: Größentabellen, Trust-Elemente, Badges, Cross-Selling-Blöcke oder Produktseitenaufbau. Eine einmalige Anpassung kostet einige Stunden, eine App kostet monatlich und bleibt im Ladepfad.",
  },
  {
    q: "Wie prüfe ich, ob eine App Umsatz bringt?",
    a: "Vor der Installation definieren, welche Kennzahl sich verändern soll, und den Zeitraum davor festhalten. Nach vier bis sechs Wochen vergleichen. Ohne diese Festlegung bleibt jedes App-Abo eine Vermutung.",
  },
];

export const Route = createFileRoute("/blog/shopify-apps-auswaehlen")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Entwicklung",
      updated: UPDATED,
      breadcrumb: { name: "Blog", path: PATH },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Blog · Entwicklung"
      title="Shopify Apps auswählen"
      intro="Der App Store macht es leicht, Funktionen zu kaufen, und schwer, sie wieder loszuwerden. Nach ein paar Jahren tragen viele Shops eine Abo-Liste mit sich herum, die monatlich Geld kostet und die Ladezeit belastet. Diese Prüfliste hilft, vor der Installation die richtige Frage zu stellen."
      source="blog-shopify-apps"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/blog", label: "Blog" }}
      sections={[
        {
          h: "Wofür Apps wirklich sinnvoll sind",
          p: [
            "Apps lohnen sich dort, wo sie eine Aufgabe übernehmen, die tief in Prozesse eingreift und laufend gepflegt werden muss: Buchhaltung und Rechnungen, Versandetiketten, Abo-Bestellungen, Bewertungen mit Verifizierung, Produktdatenpflege aus einem ERP.",
            "In diesen Fällen kaufen Sie nicht nur Code, sondern laufende Anpassung an rechtliche und technische Änderungen. Das selbst zu bauen wäre teurer als jedes Abo.",
          ],
        },
        {
          h: "Wofür sich eine Theme-Anpassung mehr lohnt",
          p: [
            "Alles, was nur anders aussehen soll, gehört ins Theme. Größentabellen, Vertrauenselemente unter dem Kaufbutton, Versandhinweise, Bundle-Darstellung, Produktseiten-Reihenfolge, Sticky-Warenkorb.",
            "Eine solche Anpassung ist einmalige Arbeit im bestehenden Theme, bleibt im Ladepfad unauffällig und verschwindet nicht, wenn ein Anbieter seine Preise erhöht oder die App einstellt.",
          ],
          list: [
            "Anzeige und Layout: Theme-Anpassung",
            "Prozesse und Datenaustausch: App",
            "Einmalige Aufgabe: Skript oder Anpassung",
            "Dauerhafte Pflege durch Dritte nötig: App",
          ],
        },
        {
          h: "Prüfliste vor jeder Installation",
          p: [
            "Vier Fragen ersparen die meisten Fehlkäufe. Wenn eine davon offenbleibt, lohnt sich das Abo meist nicht.",
          ],
          list: [
            "Welche Kennzahl soll sich verändern und um wie viel?",
            "Lädt die App Skripte auf allen Seiten oder nur dort, wo sie gebraucht wird?",
            "Was passiert beim Deinstallieren, bleiben Codereste im Theme zurück?",
            "Kann das bestehende Theme die Aufgabe mit wenigen Stunden Arbeit erledigen?",
          ],
        },
        {
          h: "Aufräumen im Bestand",
          p: [
            "Bei bestehenden Shops beginne ich mit einer Bestandsaufnahme: Welche Apps laufen, was kosten sie zusammen im Jahr, welche Skripte laden sie und welche werden noch benutzt. Häufig lassen sich mehrere Abos ersatzlos streichen.",
            "Wichtig ist das Nachräumen: Deinstallierte Apps lassen oft Codeblöcke im Theme zurück, die weiter geladen werden. Diese Reste zu entfernen bringt bei alten Shops spürbar Ladezeit zurück.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-pagespeed-optimieren", label: "Shopify Pagespeed optimieren" },
        { to: "/shopify-theme-anpassung", label: "Shopify Theme Anpassung" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten im Überblick" },
      ]}
    />
  );
}
